import { Component } from '@theme/component';

/**
 * @typedef {object} LabelItem
 * @property {string | null} id
 * @property {HTMLElement} hotspot
 * @property {HTMLElement} label
 * @property {string} side
 * @property {number} height
 * @property {number} width
 * @property {number} mediaX
 * @property {number} mediaY
 * @property {number} [x]
 * @property {number} [y]
 * @property {number} [centreX]
 * @property {number} [centreY]
 */

class ImageHotspotsComponent extends Component {
  #hoverCapable = window.matchMedia('(hover: hover) and (pointer: fine)');
  #isDesktop = window.matchMedia('(min-width: 750px)');
  #hoverOpenTimer = 0;
  #hoverCloseTimer = 0;
  #labelMode = false;
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;
  #lastWidth = 0;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#handleClick);
    this.addEventListener('mouseover', this.#handlePointerOver);
    this.addEventListener('mouseout', this.#handlePointerOut);
    document.addEventListener('keydown', this.#handleKeydown);
    document.addEventListener('click', this.#handleDocumentClick);

    if (this.dataset.placement === 'label') {
      this.#labelMode = true;
      this.addEventListener('focusin', this.#handleReveal);
      this.addEventListener('focusout', this.#handleReveal);
      this.#collectLabels();
      this.#layoutLabels();
      // A slow image changes height but not width, which the width guard would skip;
      // force a relayout once each image lands so the dots/lines line up.
      for (const img of this.querySelectorAll('img')) {
        if (!img.complete) img.addEventListener('load', () => this.#layoutLabels(true), { once: true });
      }
      this.#resizeObserver = new ResizeObserver(() => this.#layoutLabels());
      this.#resizeObserver.observe(this);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('mouseover', this.#handlePointerOver);
    this.removeEventListener('mouseout', this.#handlePointerOut);
    this.removeEventListener('focusin', this.#handleReveal);
    this.removeEventListener('focusout', this.#handleReveal);
    document.removeEventListener('keydown', this.#handleKeydown);
    document.removeEventListener('click', this.#handleDocumentClick);
    window.clearTimeout(this.#hoverOpenTimer);
    window.clearTimeout(this.#hoverCloseTimer);
    this.#resizeObserver?.disconnect();
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const close = target.closest('.image-hotspots__close');
    if (close) {
      const popup = /** @type {HTMLElement | null} */ (close.closest('.image-hotspots__popup'));
      if (popup) this.#closePopup(popup);
      return;
    }

    const trigger = target.closest('.image-hotspots__trigger');
    if (trigger) {
      event.stopPropagation();
      const isOpen = trigger.getAttribute('aria-expanded') === 'true';
      const popupId = trigger.getAttribute('aria-controls');
      const popup = /** @type {HTMLElement | null} */ (this.querySelector(`#${popupId}`));

      this.#closeAll();

      if (!isOpen && popup) {
        this.#openPopup(trigger, popup);
      }
    }
  };

  // Desktop only: open on hover without stealing focus. Touch devices keep tap-to-open.
  /** @param {MouseEvent} event */
  #handlePointerOver = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const hotspot = target.closest('.image-hotspots__hotspot');
    const label = target.closest('.image-hotspots__label');
    if (!hotspot && !label) return;

    // A dot and its label are one hoverable unit (WCAG 1.4.13); ignore moves within it.
    if (event.relatedTarget instanceof Node && this.#withinUnit(hotspot, label, event.relatedTarget)) return;

    if (this.#labelMode) {
      if (this.#isDesktop.matches) this.#setActive(this.#unitId(hotspot, label), true);
      return;
    }
    if (!this.#hoverCapable.matches || !hotspot) return;

    const trigger = hotspot.querySelector('.image-hotspots__trigger');
    if (!trigger) return;
    const popup = /** @type {HTMLElement | null} */ (this.querySelector(`#${trigger.getAttribute('aria-controls')}`));
    if (!popup) return;

    window.clearTimeout(this.#hoverCloseTimer);
    if (trigger.getAttribute('aria-expanded') === 'true') return;

    window.clearTimeout(this.#hoverOpenTimer);
    this.#hoverOpenTimer = window.setTimeout(() => {
      this.#closeAll();
      this.#openPopup(trigger, popup, { focus: false });
    }, 80);
  };

  /** @param {MouseEvent} event */
  #handlePointerOut = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const hotspot = target.closest('.image-hotspots__hotspot');
    const label = target.closest('.image-hotspots__label');
    if (!hotspot && !label) return;

    // Moving between a dot and its own label is not leaving (keeps the label hoverable).
    if (event.relatedTarget instanceof Node && this.#withinUnit(hotspot, label, event.relatedTarget)) return;

    if (this.#labelMode) {
      if (this.#isDesktop.matches) this.#setActive(this.#unitId(hotspot, label), false);
      return;
    }
    if (!this.#hoverCapable.matches || !hotspot) return;

    window.clearTimeout(this.#hoverOpenTimer);

    const popup = /** @type {HTMLElement | null} */ (hotspot.querySelector('.image-hotspots__popup'));
    if (!popup || popup.hasAttribute('hidden')) return;

    window.clearTimeout(this.#hoverCloseTimer);
    this.#hoverCloseTimer = window.setTimeout(() => {
      this.#closePopup(popup, { focus: false });
    }, 160);
  };

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    if (event.key === 'Escape') {
      this.#closeAll({ focus: true });
      return;
    }

    if (event.key === 'Tab') {
      const openPopup = this.querySelector('.image-hotspots__popup:not([hidden])');
      if (!openPopup) return;

      const focusable = /** @type {HTMLElement[]} */ (
        Array.from(openPopup.querySelectorAll(':is(button, [href], input, select, textarea, [tabindex]):not([tabindex="-1"])'))
      ).filter((element) => !element.hasAttribute('disabled'));

      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    }
  };

  /** @param {MouseEvent} event */
  #handleDocumentClick = (event) => {
    if (event.target instanceof Node && this.contains(event.target)) return;
    this.#closeAll();
  };

  /**
   * @param {Element} trigger
   * @param {HTMLElement} popup
   * @param {{ focus?: boolean }} [options]
   */
  #openPopup(trigger, popup, { focus = true } = {}) {
    trigger.setAttribute('aria-expanded', 'true');
    popup.removeAttribute('hidden');
    // Hover-opens (no focus) auto-close on mouse-out, so the close button is redundant
    // and hidden. Click/keyboard opens keep it visible and focusable.
    popup.toggleAttribute('data-hover-open', !focus);
    this.#positionPopup(popup);
    if (focus) /** @type {HTMLElement | null} */ (popup.querySelector('.image-hotspots__close'))?.focus();
  }

  /**
   * @param {HTMLElement} popup
   * @param {{ focus?: boolean }} [options]
   */
  #closePopup(popup, { focus = true } = {}) {
    const trigger = /** @type {HTMLElement | null} */ (
      popup.closest('.image-hotspots__hotspot')?.querySelector('.image-hotspots__trigger') ?? null
    );
    popup.setAttribute('hidden', '');
    popup.removeAttribute('data-position');
    popup.removeAttribute('data-hover-open');
    popup.style.removeProperty('--popup-offset');
    trigger?.setAttribute('aria-expanded', 'false');
    if (focus) trigger?.focus();
  }

  /** @param {{ focus?: boolean }} [options] */
  #closeAll({ focus = false } = {}) {
    const openPopups = /** @type {NodeListOf<HTMLElement>} */ (
      this.querySelectorAll('.image-hotspots__popup:not([hidden])')
    );
    for (const popup of openPopups) {
      this.#closePopup(popup, { focus });
    }
  }

  // Label modes: reveal a hotspot's label + connector line on keyboard focus.
  /** @param {Event} event */
  #handleReveal = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const hotspot = target.closest('.image-hotspots__hotspot');
    if (!hotspot) return;
    const trigger = hotspot.querySelector('.image-hotspots__trigger');
    if (!trigger) return;
    this.#setActive(trigger.getAttribute('aria-controls'), event.type === 'focusin');
  };

  // A hotspot dot and its label form one hoverable unit, keyed by the popup id.
  /**
   * @param {Element | null} hotspot
   * @param {Element | null} label
   * @returns {string | null}
   */
  #unitId(hotspot, label) {
    if (hotspot) return hotspot.querySelector('.image-hotspots__trigger')?.getAttribute('aria-controls') ?? null;
    return label?.getAttribute('data-for') ?? null;
  }

  /**
   * True when `related` is inside the same hotspot+label unit, so the pointer hasn't
   * really left it (WCAG 1.4.13: the revealed label stays hoverable).
   * @param {Element | null} hotspot
   * @param {Element | null} label
   * @param {Node} related
   */
  #withinUnit(hotspot, label, related) {
    const id = this.#unitId(hotspot, label);
    if (!id) return false;
    const unitHotspot = hotspot ?? this.querySelector(`[aria-controls="${id}"]`)?.closest('.image-hotspots__hotspot') ?? null;
    const unitLabel = label ?? this.querySelector(`.image-hotspots__label[data-for="${id}"]`);
    return Boolean(unitHotspot?.contains(related) || unitLabel?.contains(related));
  }

  /**
   * @param {string | null} id
   * @param {boolean} on
   */
  #setActive(id, on) {
    this.querySelector(`.image-hotspots__label[data-for="${id}"]`)?.classList.toggle('is-active', on);
    this.querySelector(`.image-hotspots__lines [data-for="${id}"]`)?.classList.toggle('is-active', on);
  }

  // Each hotspot block renders its label hidden inside itself (theme blocks can't
  // be read from section.blocks); move them into the shared labels layer.
  #collectLabels() {
    const container = this.querySelector('.image-hotspots__labels');
    if (!container) return;
    for (const label of this.querySelectorAll('.image-hotspots__hotspot .image-hotspots__label')) {
      label.removeAttribute('hidden');
      container.appendChild(label);
    }
  }

  // Place labels in four zones (left/right gutters, top/bottom bands) so they never
  // overlap, then draw a right-angled connector from each out to its dot.
  /** @param {boolean} [force] Bypass the width guard (e.g. after an image loads and changes height). */
  #layoutLabels = (force = false) => {
    if (!this.#labelMode) return;
    // We mutate paddingTop/Bottom below, which changes height and re-fires the observer;
    // guarding on width (the only thing the layout depends on) avoids that loop.
    const rect = this.getBoundingClientRect();
    if (!force && rect.width === this.#lastWidth) return;
    this.#lastWidth = rect.width;
    // Below this the image + side callouts can't fit (narrow viewport, or a narrow
    // column beside content), so fall back to tap-popups instead of cramming labels.
    const compact = rect.width < 640;
    this.toggleAttribute('data-compact', compact);
    if (compact) {
      this.style.paddingTop = '';
      this.style.paddingBottom = '';
      return;
    }
    const svg = this.querySelector('.image-hotspots__lines');
    const media = this.querySelector('.image-hotspots__media');
    if (!svg || !media) return;
    const mediaRect = media.getBoundingClientRect();

    // Read phase: gather every dot centre and label size up front, so positioning the
    // labels below never interleaves reads and writes (which would force reflows).
    const hotspots = /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('.image-hotspots__hotspot'));
    /** @type {LabelItem[]} */
    const items = [];
    for (const hotspot of hotspots) {
      const trigger = hotspot.querySelector('.image-hotspots__trigger');
      if (!trigger) continue;
      const id = trigger.getAttribute('aria-controls');
      const label = /** @type {HTMLElement | null} */ (this.querySelector(`.image-hotspots__label[data-for="${id}"]`));
      if (!label) continue;
      const dot = hotspot.querySelector('.image-hotspots__dot');
      const dotBox = dot ? dot.getBoundingClientRect() : null;
      items.push({
        id,
        hotspot,
        label,
        side: label.dataset.labelSide ?? 'auto',
        height: label.offsetHeight,
        width: label.offsetWidth,
        mediaX: dotBox ? dotBox.left + dotBox.width / 2 - mediaRect.left : 0,
        mediaY: dotBox ? dotBox.top + dotBox.height / 2 - mediaRect.top : 0,
      });
    }

    // Resolve auto -> left/right by the dot's horizontal position within the image.
    for (const item of items) {
      if (item.side === 'auto') item.side = item.mediaX < mediaRect.width / 2 ? 'left' : 'right';
    }

    // Reserve top/bottom bands sized to the tallest label in each.
    const bandGap = 16;
    const topItems = items.filter((item) => item.side === 'top');
    const bottomItems = items.filter((item) => item.side === 'bottom');
    const topBand = topItems.length ? Math.max(...topItems.map((item) => item.height)) + bandGap : 0;
    const bottomBand = bottomItems.length ? Math.max(...bottomItems.map((item) => item.height)) + bandGap : 0;

    // Dot coordinates relative to the component (the top band pushes the image down).
    const mediaOffsetX = mediaRect.left - rect.left;
    for (const item of items) {
      item.x = item.mediaX + mediaOffsetX;
      item.y = item.mediaY + topBand;
    }

    // Write phase: apply the bands, set sides, then position each label.
    this.style.paddingTop = `${topBand}px`;
    this.style.paddingBottom = `${bottomBand}px`;
    for (const item of items) item.label.dataset.labelSide = item.side;

    const minGap = 14;
    const contentHeight = mediaRect.height + topBand + bottomBand;

    // Left/right gutters: distribute vertically within the image band.
    for (const side of ['left', 'right']) {
      const group = items.filter((item) => item.side === side).sort((a, b) => (a.y ?? 0) - (b.y ?? 0));
      let prevBottom = topBand - minGap;
      for (const item of group) {
        const label = item.label;
        label.style.left = side === 'left' ? '0' : 'auto';
        label.style.right = side === 'left' ? 'auto' : '0';
        let top = Math.max(topBand, (item.y ?? 0) - item.height / 2);
        if (top < prevBottom + minGap) top = prevBottom + minGap;
        top = Math.min(top, mediaRect.height + topBand - item.height);
        label.style.top = `${top}px`;
        label.style.bottom = 'auto';
        prevBottom = top + item.height;
        item.centreY = top + item.height / 2;
      }
    }

    // Top/bottom bands: distribute horizontally across the width.
    for (const zone of ['top', 'bottom']) {
      const group = items.filter((item) => item.side === zone).sort((a, b) => (a.x ?? 0) - (b.x ?? 0));
      let prevRight = -Infinity;
      for (const item of group) {
        const label = item.label;
        label.style.top = zone === 'top' ? '0' : 'auto';
        label.style.bottom = zone === 'top' ? 'auto' : '0';
        let left = (item.x ?? 0) - item.width / 2;
        if (left < prevRight + minGap) left = prevRight + minGap;
        left = Math.max(0, Math.min(left, rect.width - item.width));
        label.style.left = `${left}px`;
        label.style.right = 'auto';
        prevRight = left + item.width;
        item.centreX = left + item.width / 2;
      }
    }

    // Right-angled connectors: H-V-H for side labels, V-H-V for top/bottom. Label edges
    // are derived from their known box, so no getBoundingClientRect after positioning.
    svg.setAttribute('viewBox', `0 0 ${rect.width} ${contentHeight}`);
    svg.replaceChildren();
    const gap = 12;
    for (const item of items) {
      const x = item.x ?? 0;
      const y = item.y ?? 0;
      const centreX = item.centreX ?? 0;
      const centreY = item.centreY ?? 0;
      let points;
      if (item.side === 'left' || item.side === 'right') {
        const edgeX = item.side === 'left' ? item.width + gap : rect.width - item.width - gap;
        const midX = (edgeX + x) / 2;
        points = `${edgeX},${centreY} ${midX},${centreY} ${midX},${y} ${x},${y}`;
      } else {
        const edgeY = item.side === 'top' ? item.height + gap : contentHeight - item.height - gap;
        const midY = (edgeY + y) / 2;
        points = `${centreX},${edgeY} ${centreX},${midY} ${x},${midY} ${x},${y}`;
      }
      const connector = document.createElementNS('http://www.w3.org/2000/svg', 'polyline');
      connector.setAttribute('points', points);
      connector.setAttribute('fill', 'none');
      connector.setAttribute('data-for', item.id ?? '');
      svg.appendChild(connector);
    }
  };

  /** @param {HTMLElement} popup */
  #positionPopup(popup) {
    const wrapperRect = this.getBoundingClientRect();
    const hotspot = popup.closest('.image-hotspots__hotspot');
    if (!hotspot) return;
    const hotspotRect = hotspot.getBoundingClientRect();
    const popupRect = popup.getBoundingClientRect();

    const spaceAbove = hotspotRect.top - wrapperRect.top;
    const spaceBelow = wrapperRect.bottom - hotspotRect.bottom;

    if (spaceAbove < popupRect.height + 20 && spaceBelow > spaceAbove) {
      popup.setAttribute('data-position', 'below');
    }

    // Clamp horizontally to the viewport, not the image column, so the popup
    // never overruns the screen edge even when the image sits in a narrow column.
    const hotspotCenterX = hotspotRect.left + hotspotRect.width / 2;
    const halfWidth = popupRect.width / 2;
    const margin = 8;

    let offset = 0;
    const leftEdge = hotspotCenterX - halfWidth;
    const rightEdge = hotspotCenterX + halfWidth;
    // clientWidth excludes the scrollbar, so the popup never tucks under it.
    const viewportWidth = document.documentElement.clientWidth;

    if (leftEdge < margin) {
      offset = margin - leftEdge;
    } else if (rightEdge > viewportWidth - margin) {
      offset = viewportWidth - margin - rightEdge;
    }

    if (offset !== 0) {
      popup.style.setProperty('--popup-offset', `${offset}px`);
    }
  }
}

if (!customElements.get('image-hotspots-component')) {
  customElements.define('image-hotspots-component', ImageHotspotsComponent);
}
